/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../Audio/AudioManager';
import DtModObservable from '../Game/DtModObservable';
import Currency, { CurrencyType } from '../Localization/Currency';
import ObservableCurrencyComponent from '../Localization/ObservableCurrencyComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableDoubleComponent from '../Utility/Component/Observable/ObservableDoubleComponent';
import ObservableEvent from '../Utility/Component/Observable/ObservableEvent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Decorators from '../Utility/Decorators';
import BetManager from './BetManager';
import MusicalBangup from './MusicalBangup';
import MusicalBangupInfo from './MusicalBangupInfo';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Controls the bangup effect when a payout occurs.
 */
@cc._decorator.ccclass
export default class Bangup extends StateDependencyComponent
{
    /** On which state should the bangup play. */
    @property({ type: StateReference, visible: true })
    private _bangupOnState: StateReference = new StateReference(StateID.Award, true);

    /** Any additional states on which a bangup should play for an award. */
    @property({ type: [StateReference], visible: true })
    private _altAwardStates: StateReference[] = [];

    /** On which states should any in-progress bangup be cleared and cancelled out of. */
    @property({ type: [StateReference], visible: true })
    private _clearStates: StateReference[] = [];

    /** The starting speed, in credits per second, of how fast the bangup should go. */
    @property({ type: cc.Float, visible: true })
    private _bangupSpeed: number = 20;

    /** The accelleration of the bangup speed, in credits per second squared. */
    @property({ type: cc.Float, visible: true })
    private _bangupAcceleration: number = 10;

    @property()
    protected bangupAudioCue: string = 'BangupSound';

    @property()
    protected bangupCompleteAudioCue: string = 'BangupComplete';

    @property(cc.Float)
    protected betChangeClearDelaySs: number = 1.5;

    /** Cumulative bangup continues from current value instead of resetting to zero */
    @property
    protected isCumulative: boolean = false;
    public set IsCumulative(value: boolean) { this.isCumulative = value; }
    public get IsCumulative(): boolean { return this.isCumulative; }

    /** Should the bangup affect the balance meter as well. */
    @property()
    protected updateBalance: boolean = true;
    /** Public getter for whether update balance. */
    public get UpdateBalance(): boolean { return this.updateBalance; }

    /** The observable reference to the current award value. */
    @property({ type: InjectableComponent, visible: true })
    private _currentValueRef: InjectableComponent<ObservableCurrencyComponent>
        = new InjectableComponent<ObservableCurrencyComponent>(ReferenceGroup.MainGame, 'WinMeter.CurrentAward');

    /** Convenience accessor for the current value of the award observable. */
    protected get currentValueObservable(): ObservableCurrencyComponent { return this._currentValueRef.Value; }
    /** Accessor for the current value of the observable award value. */
    protected get currentValue(): Currency { return this.currentValueObservable.Currency; }

    @property(cc.Float)
    protected bangsPerSecond: number = 30;

    /** @hidden */
    private _audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent<AudioManager>();
    /** @hidden */
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent<BetManager>();
    /** @hidden */
    private _musicalBangupRef: InjectableComponent<MusicalBangup> = new InjectableComponent<MusicalBangup>();
    public get BetManagerRef(): InjectableComponent<BetManager> { return this._betManagerRef; }

    /** @hidden */
    private _dtModRef: InjectableComponent<DtModObservable> = new InjectableComponent();

    protected activeDenomObservable: ObservableDoubleComponent = null;

    /** The current speed, in credits per second, at which the meters should bangup. */
    private _bangupRate: Currency = null;

    private _fractionalBangupRate: number = 0;

    /** To be executed when the current bangup has completed. */
    private _fnOnComplete: Function;
    /** Has the current bangup completed. */
    private _isBangupComplete: boolean = true;
    private _hasBanged: boolean = false;
    private _isClearing: boolean = false;
    /** Stores fractional currency / credits as we never want to add that to the balance. */
    private _pendingBalanceAdd: Currency = null;
    /** The amount of fractional currency currently pending adding to the balance. */
    public get PendingBalanceAdd(): Currency { return this._pendingBalanceAdd; }
    /** The target value that is being banged up towards. */
    private _targetValue: Currency = null;
    /** The target value, in credits or cash based on current UI. */
    public get TargetValue(): Currency { return this._targetValue; }

    private _elapsedSinceLastBang: number = 0;

    private _hasMusicalBangup: boolean = false;
    private _musicalBangupRate: Currency = null;

    /** @hidden */
    protected setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
        this._audioManagerRef.SetContext(AudioManager);
        this._currentValueRef.SetContext(ObservableCurrencyComponent);
        this._musicalBangupRef.SetContext(MusicalBangup);
        this._dtModRef.SetContext(DtModObservable);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._altAwardStates, this._clearStates, [this._bangupOnState]);
        this.checkDependencies();

        this._betManagerRef.WithValue(mgr =>
        {
            mgr.BetObservable.OnChanged(this.betChangedHandler);
            this.destructors.push(() => mgr.BetObservable.OffChanged(this.betChangedHandler));

            this.activeDenomObservable = mgr.ActiveDenomObservable;
        });

        this._targetValue = new Currency(this.activeDenomObservable, 0);
        this._pendingBalanceAdd = new Currency(this.activeDenomObservable, 0);
        this._bangupRate = new Currency(this.activeDenomObservable, 0);
    }

    /** @hidden */
    protected update(dt): void
    {
        super.update(dt);

        this._elapsedSinceLastBang += dt;

        if (this._elapsedSinceLastBang < 1 / this.bangsPerSecond) { return; }

        let activeStateReference: StateReference =
            (this._bangupOnState.Value.IsCurrentState())
                ? this._bangupOnState
                : this._altAwardStates.find(StateReference.IsCurrentFilter);

        if (!activeStateReference) { return; }

        let modDT: number = this._dtModRef.Value.Multiply(this._elapsedSinceLastBang);

        let difference: Currency = this._targetValue
            .Difference(this.currentValue);

        if (difference.GetValue() > 0)
        {
            // Increase acceleration.
            if (!this._hasMusicalBangup)
            {
                this._fractionalBangupRate += this._bangupAcceleration
                    * modDT
                    * this._betManagerRef.Value.CurrentBetScaler;
                let accel: Currency
                    = new Currency(this.activeDenomObservable, this._fractionalBangupRate, CurrencyType.CREDITS);
                this._bangupRate.Set(accel);
            }
            else
            {
                this._bangupRate
                    = new Currency(this._musicalBangupRate, this._musicalBangupRate.GetValue() * modDT);
            }

            // Is difference less than next bangup?
            if (difference.Difference(this._bangupRate).GetValue() <= 0)
            {
                this.addToPendingBalance(difference, true);
                difference.Clear();
            }
            else
            {
                this.addToPendingBalance(this._bangupRate);
                difference = this._targetValue.Difference(this.currentValue);
            }
        }

        // Bangup Complete
        if (difference.GetValue() === 0)
        {
            this.completeBangup();
            this.completeState(activeStateReference.Value);

            if (this._fnOnComplete != null)
            {
                this._fnOnComplete();
            }
        }
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        const isBangupState: boolean =
            this._bangupOnState.Value.IsCurrentState() || this._altAwardStates.some(StateReference.IsCurrentFilter);
        if (isBangupState && !this._hasMusicalBangup)
        {
            this.resetBangupRate();
        }

        if (this._clearStates.some(stateRef => stateRef.Value === event.State))
        {
            this.completeBangup();

            this.Clear();
            this.completeState(event.State);
        }
    }

    /** Clears the current bangup. */
    public Clear = (): void =>
    {
        this.currentValue.Clear();
        this._targetValue.Clear();
        this._pendingBalanceAdd.Clear();
        this._fractionalBangupRate = 0;
        this._elapsedSinceLastBang = 0;
        this._hasBanged = this._isClearing = false;
    }

    /** Auto-complets the bangup when the the reels are quickstopped. */
    public QuickBang = (): void =>
    {
        if (this._isBangupComplete)
        {
            this.completeState(this._bangupOnState.Value);
            return;
        }

        const remainingDiff: Currency = this._targetValue.Difference(this.currentValue);

        this.addToPendingBalance(remainingDiff);

        this.completeBangup(true);
        this.completeState(this._bangupOnState.Value);
    }

    @Decorators.BindThis()
    private betChangedHandler(event: ObservableEvent<number>): void
    {
        if (!event.RootValueChanged || !this._hasBanged || this._isClearing) { return; }

        if (this._isBangupComplete)
        {
            this._currentValueRef.WithValue(obs =>
            {
                if (obs.Value <= 0) { return; }
                this.Clear();
            });
            return;
        }

        this._isClearing = true;
        this.QuickBang();
        this.scheduleOnce(() => this.Clear(), this.betChangeClearDelaySs);
    }

    private addToPendingBalance = (amount: Currency, lastBang: boolean = false): void =>
    {
        if ((amount.GetValue() === 0) && !lastBang) { return; }

        this._pendingBalanceAdd.Add(amount);
        this.currentValue.Add(amount);

        this._elapsedSinceLastBang = 0;

        if (!this.updateBalance) { return; }

        this._betManagerRef.Value.AddBalance(this._pendingBalanceAdd);
        this._pendingBalanceAdd.Clear();
    }

    /**
     * For triggering QuickBang off of an event.
     * @param event The triggering event.
     * @param detail (Ignored) Any additional data passed with the event.
     */
    public OnQuickBangEvent(event: cc.Event.EventCustom, detail: any): void
    {
        this.QuickBang();
    }

    /**
     * Adds additional award to the target value of the current bangup and starts
     * it back up again if it has already completed.
     * @param credits The amount, in credits, to increase the target value by.
     */
    public BangupAdditionalValue(value: Currency): void
    {
        if (value.GetValue() < 1) { return; }

        this._elapsedSinceLastBang = 0;

        this._targetValue.Add(value);
        this.startBangup(value);
    }

    private startBangup(value: Currency): void
    {
        this.setMusicalBangupRate(value);
        this._isBangupComplete = false;
        this._hasBanged = true;
    }

    /**
     * Sets target bangup to given value.
     * @param value Currency value to bangup to.
     */
    public SetCurrent(value: Currency): void
    {
        if (value.GetValue() < 0) { return; }

        this.Clear();

        this._targetValue.Set(value);
        this._currentValueRef.Value.Currency.Set(value);

        this._isBangupComplete = true;
    }

    /**
     * Sets target bangup to given value.
     * @param value Currency value to bangup to.
     */
    public SetTarget(value: Currency): void
    {
        if (value.GetValue() < 1) { return; }

        this.Clear();

        this._targetValue.Set(value);
        this.startBangup(value);
    }

    /**
     * Sets the bangup rate based on [[MusicalBangup]] component.
     * @param value Set to [[Currency]] value.
     */
    private setMusicalBangupRate = (value: Currency): void =>
    {
        if (this._hasMusicalBangup)
        {
            this._musicalBangupRate = this._musicalBangupRef.Value.PlayBangupAudio(value);
        }
        else
        {
            this._audioManagerRef.Value.TriggerSound(null, 'BangupSound');
        }
    }

    /**
     * Stops bangup sounds, plays the complete sound, and flips the complete flag.
     * @hidden
     */
    private completeBangup(isQuickBang: boolean = false): void
    {
        if (this._isBangupComplete) { return; }

        this._musicalBangupRef.WithValue(bangup =>
        {
            const lastBangup: MusicalBangupInfo = bangup.LastBangupInfo;

            // For musical, only play bangup stop on quick bang.
            if (isQuickBang && lastBangup.Ratio > bangup.MinRatioBangupComplete)
            {
                bangup.StopBangupAudio();
                this.triggerBangupCompleteAudio();
            }
        }, () => this.triggerBangupCompleteAudio());
        this._isBangupComplete = true;
    }

    /** @hidden */
    private triggerBangupCompleteAudio(): void
    {
        this._audioManagerRef.WithValue(mgr =>
        {
            mgr.TriggerSound(null, this.bangupCompleteAudioCue);
            if (this.bangupAudioCue) { mgr.StopTrigger(null, this.bangupAudioCue); }
        });
    }

    /**
     * Ensures neccessary dependencies are present.
     * @hidden
     */
    private checkDependencies = (): void =>
    {
        this._hasMusicalBangup = !!this._musicalBangupRef.Value;

        if (!this._betManagerRef.Value)
        {
            CLog.error("No BetManager configured.", this, LogTypes.Initialization);
        }

        if (!this.currentValueObservable)
        {
            CLog.error("No current award observable configured. Disabled.", this, LogTypes.Initialization);
            this.enabled = false;
        }
    }

    /** Resets bangup rate to use current denom. */
    private resetBangupRate = (): void =>
    {
        this._bangupRate.SetValue(this._bangupSpeed, CurrencyType.CREDITS);
    }
}
